import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container, Box, Typography, TextField, Button, Paper, Avatar, Chip,
  Divider, CircularProgress, Alert, LinearProgress, Stack
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  AccountBalanceWallet as WalletIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  CameraAlt as CameraIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import SplitSmartAPI from '../../services/splitSmartAPI';

const CATEGORY_EMOJIS = {
  trip: '🏖️', roommates: '🏠', dinner: '🍽️',
  event: '🎉', work: '💼', other: '📦'
};

/**
 * JoinGroupPage — Public guest-access page for shared SplitSmart groups.
 *
 * Accessed via /group/join/:shareCode. No authentication required.
 * Allows guests to view group details, members, linked bills, and join by name/email.
 */
const JoinGroupPage = () => {
  const { shareCode } = useParams();
  const theme = useTheme();

  const [group, setGroup] = useState(null);
  const [bills, setBills] = useState([]);
  const [billCount, setBillCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [hasJoined, setHasJoined] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ── Load group data ──────────────────────────────────────────────────
  useEffect(() => {
    const loadGroup = async () => {
      setLoading(true);
      try {
        const result = await SplitSmartAPI.getGroupByShareCode(shareCode);
        if (result.success) {
          setGroup(result.group);
          setBills(result.bills || []);
          setBillCount(result.billCount || 0);
        } else {
          setError('Group not found');
        }
      } catch {
        setError('Unable to load group. The link may be invalid or expired.');
      }
      setLoading(false);
    };
    loadGroup();
  }, [shareCode]);

  // ── Join handler ─────────────────────────────────────────────────────
  const handleJoin = async () => {
    const trimmedName = guestName.trim();
    if (!trimmedName) return;

    setSubmitting(true);
    setError(null);
    try {
      const result = await SplitSmartAPI.joinGroupByShareCode(shareCode, {
        name: trimmedName,
        email: guestEmail.trim() || undefined
      });
      if (result.success) {
        setGroup(result.group);
        setHasJoined(true);
      } else {
        setError(result.message || 'Failed to join group');
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Something went wrong. Please try again.';
      setError(msg);
    }
    setSubmitting(false);
  };

  // ── Derived values ───────────────────────────────────────────────────
  const categoryEmoji = CATEGORY_EMOJIS[group?.category] || '📦';
  const creatorName = group?.creator?.name || group?.creator?.email || 'Someone';
  const currency = group?.currency || 'EUR';
  const totalBillAmount = bills.reduce((sum, b) => sum + (b.totalAmount || 0), 0);
  const budgetProgress = group?.budget > 0
    ? Math.min(100, ((group.budgetSpent || 0) / group.budget) * 100)
    : 0;

  // ── Loading state ────────────────────────────────────────────────────
  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center', minHeight: '100vh' }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography variant="body1" color="text.secondary" fontWeight={500}>
          Loading group details…
        </Typography>
      </Container>
    );
  }

  // ── Error / Not found state ──────────────────────────────────────────
  if (error && !group) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, minHeight: '100vh' }}>
        <Paper elevation={0} sx={{
          p: 5, textAlign: 'center', borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        }}>
          <Box sx={{
            width: 72, height: 72, borderRadius: '50%', mx: 'auto', mb: 2.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(theme.palette.error.main, 0.08),
          }}>
            <GroupIcon sx={{ fontSize: 36, color: 'error.main' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
            Group Not Found
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            This share link may have expired or been disabled.
          </Typography>
          <Button variant="contained" href="/"
            sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700, minHeight: 48, px: 4 }}>
            Go to SoldiKeeper
          </Button>
        </Paper>
      </Container>
    );
  }

  // ── Main render ──────────────────────────────────────────────────────
  return (
    <Container maxWidth="sm" sx={{ pt: 2, pb: 4, minHeight: '100vh' }}>

      {/* ═══════════════════ Header / Branding ═══════════════════ */}
      <Paper elevation={0} sx={{
        textAlign: 'center', py: 4, px: 3, mb: 3, borderRadius: 3,
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
        position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'absolute', top: -40, right: -30,
          width: 140, height: 140, borderRadius: '50%',
          bgcolor: alpha(theme.palette.primary.main, 0.05),
        }} />
        <Box sx={{
          position: 'absolute', bottom: -30, left: -20,
          width: 100, height: 100, borderRadius: '50%',
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        }} />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{
            width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
          }}>
            <WalletIcon sx={{ fontSize: 30, color: 'primary.main' }} />
          </Box>
          <Typography variant="h5" sx={{
            fontWeight: 800, color: 'primary.main', letterSpacing: '-0.02em',
          }}>
            SoldiKeeper
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mb: 2 }}>
            SplitSmart — Group expenses made easy
          </Typography>
          <Chip
            icon={<PersonIcon sx={{ fontSize: 16 }} />}
            label={`Invited by ${creatorName}`}
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              fontWeight: 600, fontSize: '0.8rem', height: 32,
            }}
          />
        </Box>
      </Paper>

      {/* ═══════════════════ Group Summary — Hero Card ═══════════════════ */}
      <Paper elevation={0} sx={{
        p: 3, borderRadius: 3, mb: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Typography variant="h6" component="span" sx={{ fontSize: '1.6rem' }}>
            {categoryEmoji}
          </Typography>
          <Typography variant="h4" sx={{
            fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2,
          }}>
            {group.name}
          </Typography>
        </Box>

        {group.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {group.description}
          </Typography>
        )}

        {/* Summary chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip
            icon={<GroupIcon sx={{ fontSize: 16 }} />}
            label={`${group.members?.length || 0} members`}
            size="small"
            sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), fontWeight: 600, height: 28 }}
          />
          <Chip
            label={currency}
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.info?.main || theme.palette.primary.main, 0.08),
              fontWeight: 600, height: 28,
            }}
          />
          {group.category && (
            <Chip
              label={`${categoryEmoji} ${group.category}`}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.warning.main, 0.08),
                fontWeight: 600, height: 28, textTransform: 'capitalize',
              }}
            />
          )}
        </Box>

        {/* Budget progress */}
        {group.budget > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                Budget
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {currency} {(group.budgetSpent || 0).toFixed(2)} / {currency} {group.budget.toFixed(2)}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={budgetProgress}
              color={budgetProgress > 90 ? 'error' : budgetProgress > 70 ? 'warning' : 'primary'}
              sx={{
                height: 6, borderRadius: 3,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                '& .MuiLinearProgress-bar': { borderRadius: 3 },
              }}
            />
          </Box>
        )}

        {/* Bills summary */}
        {billCount > 0 && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, mt: 2,
            p: 1.5, borderRadius: 2,
            bgcolor: alpha(theme.palette.success.main, 0.06),
          }}>
            <Avatar sx={{
              width: 36, height: 36,
              bgcolor: alpha(theme.palette.success.main, 0.12),
              color: 'success.main',
            }}>
              <ReceiptIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight={700}>
                {billCount} bill{billCount !== 1 ? 's' : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {currency} {totalBillAmount.toFixed(2)} total
              </Typography>
            </Box>
          </Box>
        )}

        {group.createdAt && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
            Created {new Date(group.createdAt).toLocaleDateString(undefined, {
              month: 'short', day: 'numeric', year: 'numeric',
            })}
          </Typography>
        )}
      </Paper>

      {/* ═══════════════════ Members ═══════════════════ */}
      {group.members?.length > 0 && (
        <Paper elevation={0} sx={{
          p: 3, borderRadius: 3, mb: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
            Members ({group.members.length})
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {group.members.map((member, i) => (
              <Chip
                key={member._id || member.email || i}
                avatar={
                  <Avatar sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.15),
                    color: 'primary.main',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                  }}>
                    {(member.name || member.email || '?').charAt(0).toUpperCase()}
                  </Avatar>
                }
                label={member.name || member.email}
                variant="outlined"
                sx={{
                  fontWeight: 600, fontSize: '0.82rem', height: 36,
                  borderColor: alpha(theme.palette.divider, 0.15),
                }}
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* ═══════════════════ Error Alert ═══════════════════ */}
      {error && (
        <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ═══════════════════ Join Card ═══════════════════ */}
      {!hasJoined ? (
        <Paper elevation={0} sx={{
          p: 3, borderRadius: 3, mb: 2,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
            Join this group
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Enter your name to start sharing expenses
          </Typography>

          <Stack spacing={2}>
            <TextField
              label="Your Name"
              placeholder="e.g. Alex"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              fullWidth
              required
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              label="Email (optional)"
              placeholder="alex@example.com"
              type="email"
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleJoin}
              disabled={!guestName.trim() || submitting}
              endIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIcon />}
              sx={{
                borderRadius: 3, textTransform: 'none', fontWeight: 700,
                minHeight: 48, fontSize: '1rem',
                boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.25)}`,
              }}
            >
              {submitting ? 'Joining…' : 'Join Group'}
            </Button>
          </Stack>
        </Paper>
      ) : (
        /* ═══════════════════ Success State ═══════════════════ */
        <Paper elevation={0} sx={{
          p: 3, borderRadius: 3, mb: 2, textAlign: 'center',
          border: `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
          bgcolor: alpha(theme.palette.success.main, 0.04),
        }}>
          <Box sx={{
            width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(theme.palette.success.main, 0.12),
          }}>
            <CheckCircleIcon sx={{ fontSize: 36, color: 'success.main' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
            You're in! 🎉
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You've joined <strong>{group.name}</strong>. Download SoldiKeeper to track expenses,
            settle debts, and stay on top of group spending.
          </Typography>
        </Paper>
      )}

      {/* ═══════════════════ SoldiKeeper Promo Footer ═══════════════════ */}
      <Paper elevation={0} sx={{
        borderRadius: 3, overflow: 'hidden', mt: 3, mb: 2,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        bgcolor: alpha(theme.palette.primary.main, 0.02),
        position: 'relative',
      }}>
        <Box sx={{
          position: 'absolute', top: -20, right: -20,
          width: 100, height: 100, borderRadius: '50%',
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        }} />
        <Box sx={{ p: 3, textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <Box sx={{
            width: 52, height: 52, borderRadius: '50%', mx: 'auto', mb: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
          }}>
            <WalletIcon sx={{ fontSize: 28, color: 'primary.main' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
            Love splitting expenses?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            SoldiKeeper gives you the full picture of your money
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3, textAlign: 'left' }}>
            {[
              { icon: <WalletIcon sx={{ fontSize: 20 }} />, text: 'Track all your expenses in one place', color: theme.palette.primary.main },
              { icon: <TrendingUpIcon sx={{ fontSize: 20 }} />, text: 'Smart budgets & financial insights', color: theme.palette.success.main },
              { icon: <CameraIcon sx={{ fontSize: 20 }} />, text: 'Scan receipts with AI', color: theme.palette.warning.main },
              { icon: <GroupIcon sx={{ fontSize: 20 }} />, text: 'Unlimited group splits with SplitSmart', color: theme.palette.info?.main || theme.palette.primary.main },
            ].map((feature, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{
                  width: 36, height: 36,
                  bgcolor: alpha(feature.color, 0.1), color: feature.color,
                }}>
                  {feature.icon}
                </Avatar>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {feature.text}
                </Typography>
              </Box>
            ))}
          </Box>

          <Button fullWidth variant="contained" href="/register"
            sx={{
              borderRadius: 3, textTransform: 'none', fontWeight: 700,
              minHeight: 52, fontSize: '1.05rem', mb: 2,
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
            }}>
            Create Your Free Account
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Join 10,000+ users managing money smarter
          </Typography>
        </Box>
      </Paper>

      {/* ═══════════════════ Brand Footer ═══════════════════ */}
      <Box sx={{ textAlign: 'center', py: 3, pb: 4 }}>
        <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 500 }}>
          © {new Date().getFullYear()} SoldiKeeper — Your money, simplified
        </Typography>
      </Box>
    </Container>
  );
};

export default JoinGroupPage;
